import * as Cl from '@design-system/component-library';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { OfferSubCategory } from '../../common/enums';
import { monthlyChargesMsg, oneTimePaymentMsg, productsMsg, quantityMsg, t } from '../../common/i18n/index.js';
import { getActiveAccountMasterId } from '../../selfservice/common/localStorageUtils.js';
import { Table } from '../Table/index.js';
import { getMonthlyCharges, getOneTimeCharges, getQuantity } from './QuotesCommonUtilsCoCo.js';
import type { AddonProductOrderItem } from '../../generated/api/addonProductOrderItem.js';
import type { ProposalProductOffering } from '../../generated/api/proposalProductOffering.js';
import type { ProposalProductOrderItem } from '../../generated/api/proposalProductOrderItem.js';
import type { ReactNode } from 'react';
import './QuoteProductOrderItemsCoCo.scss';

const getLinkByCategory = (to: string, productName: string) => {
  const mdmId = getActiveAccountMasterId();
  const mdmIdQuery = mdmId ? `?mdmId=${encodeURIComponent(mdmId)}` : '';
  return (
    <Link className="of-quote-link-inline" to={`${to}${mdmIdQuery}`} target="_blank" rel="noopener noreferrer">
      {productName}
      <Cl.Icon icon="external-link" size="s" type="regular" color="blue-600" />
    </Link>
  );
};

const getProductLink = (productOffering: ProposalProductOffering): ReactNode => {
  const productName = productOffering.name ?? '';
  const subCategory = productOffering?.type?.subcategory;

  switch (subCategory) {
    case OfferSubCategory.MOBILE_VOICE:
      return getLinkByCategory(paths.PS_MOBILE_SUBSCRIPTION_NEW, productName);
    case OfferSubCategory.MOBILE_BROADBAND:
      return getLinkByCategory(paths.PS_BROADBAND_SUBSCRIPTION_NEW, productName);
    default:
      return productName;
  }
};

interface QuoteProductOrderItemsCoCoProps {
  productOrderItems: ProposalProductOrderItem[];
}

type QuoteProductOrderColumns = {
  products: ReactNode;
  monthlyCharges: string;
  oneTimePayment: string;
  quantity: string;
};

const mapProductOrderItemColumns = (
  item: ProposalProductOrderItem | AddonProductOrderItem,
  quantity: number,
  isAddon = false
): QuoteProductOrderColumns => {
  const productName = item.productOffering.name ?? '';

  return {
    products: isAddon ? (
      <span className={`${dsClass.DISPLAY_INLINE_BLOCK} ${dsClass.MARGIN_LEFT_3}`}>{productName}</span>
    ) : (
      getProductLink(item.productOffering)
    ),
    monthlyCharges: getMonthlyCharges(item.price, quantity),
    oneTimePayment: getOneTimeCharges(item.price, quantity),
    quantity: getQuantity(quantity),
  };
};

const getAddonRows = (addonItems?: AddonProductOrderItem[]): QuoteProductOrderColumns[] => {
  return addonItems?.map(item => mapProductOrderItemColumns(item, 1, true)) ?? [];
};

const getProductOrdersByRow = (productOrderItems: ProposalProductOrderItem[]) => {
  return (
    productOrderItems.flatMap(item => {
      const productRow = mapProductOrderItemColumns(item, item.quantity, false);
      const bundledAddonRow = getAddonRows(item.bundledAddonProductOrderItems);
      const commercialAddonRow = getAddonRows(item.commercialAddonProductOrderItems);

      return [productRow, ...bundledAddonRow, ...commercialAddonRow];
    }) ?? []
  );
};

export const QuoteProductOrderItemsCoCo = ({ productOrderItems }: QuoteProductOrderItemsCoCoProps) => {
  const productOrderColumns = [
    { key: 'products', label: t.IY4Z(productsMsg), width: '25%' },
    { key: 'monthlyCharges', label: t.P6BC(monthlyChargesMsg), width: '25%' },
    { key: 'oneTimePayment', label: t.ASEI(oneTimePaymentMsg), width: '25%' },
    { key: 'quantity', label: t.M0W7(quantityMsg), width: '25%' },
  ];

  return (
    <div className="of-quote-table">
      <Table
        noMarginTop={true}
        columns={productOrderColumns}
        rows={getProductOrdersByRow(productOrderItems)}
        valignText="middle"
      />
    </div>
  );
};
